/*
 * <fb-drift-ribbon> - the favourites hero (sections/fb-favourites-hero.liquid).
 * The ribbon drifts left endlessly; hover eases it to rest, a drag scrubs it and a
 * flick flings it; a pause button, keyboard focus, being off screen or a hidden tab
 * stop it. prefers-reduced-motion (or one product, or speed 0) gives a plain
 * swipeable row with arrows. Copies for the seamless loop are aria-hidden and untabbable
 * (not inert: a copy on screen is a real card to a shopper and takes clicks).
 */
(() => {
  if (customElements.get('fb-drift-ribbon')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FINE_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const EASE = 0.18; // s: how fast the drift eases toward its target speed
  const FLING_EASE = 0.6; // s: how long a flick coasts
  const DRAG_SLOP = 6; // px a press may move before it becomes a drag
  const MAX_FLING = 2400; // px/s

  class FbDriftRibbon extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-fb-viewport]');
      this.track = this.querySelector('[data-fb-track]');
      if (!this.viewport || !this.track) return;
      this.toggle = this.querySelector('[data-fb-toggle]');
      this.arrows = this.querySelector('[data-fb-arrows]');
      this.originals = [...this.track.children].filter((li) => !li.hasAttribute('data-fb-clone'));
      this.speed = Math.max(0, Number(this.dataset.speed) || 0);
      this.x = 0;
      this.v = 0;
      this.ease = EASE;
      this.userPaused = false;
      this.hovering = false;
      this.focused = false;
      this.dragging = false;
      this.pressed = false;
      this.inView = true;
      this.abort = new AbortController();
      const opt = { signal: this.abort.signal };
      this.toggle?.addEventListener('click', () => this.setUserPaused(!this.userPaused), opt);
      this.arrows?.querySelector('[data-fb-prev]')?.addEventListener('click', () => this.step(-1), opt);
      this.arrows?.querySelector('[data-fb-next]')?.addEventListener('click', () => this.step(1), opt);
      REDUCED.addEventListener('change', () => this.setMode(), opt);
      this.setMode();
      this.observeBadges();
      this.setupLetters();
    }

    disconnectedCallback() {
      // Moved in the page (a section reorder in the theme editor) it reconnects:
      // undo everything so connectedCallback starts clean, without the old copies.
      this.abort?.abort();
      this.stop();
      this.io?.disconnect();
      this.ro?.disconnect();
      this.badgeIo?.disconnect();
      clearInterval(this.letterTimer);
      this.removeClones();
      this.track.style.transform = '';
      this.wired = false;
      this.focused = false;
      this.hovering = false;
    }

    get isStatic() {
      return this.classList.contains('is-static');
    }

    setMode() {
      this.stop();
      this.removeClones();
      this.x = 0;
      this.v = 0;
      this.track.style.transform = '';
      const staticMode = REDUCED.matches || this.speed === 0 || this.originals.length < 2;
      this.classList.toggle('is-static', staticMode);
      if (this.arrows) this.arrows.hidden = !staticMode;
      if (this.toggle) this.toggle.hidden = staticMode;
      if (staticMode) {
        this.dataset.state = 'static';
        return;
      }
      this.addClones();
      if (!this.wired) this.wire();
      this.measure();
      this.kick();
    }

    addClones() {
      const setWidth = Math.max(this.track.scrollWidth, 1);
      const copies = Math.max(1, Math.ceil((this.viewport.clientWidth * 2) / setWidth));
      for (let i = 0; i < copies; i += 1) {
        for (const item of this.originals) {
          const clone = item.cloneNode(true);
          // Hidden from screen readers and the tab order, but NOT inert: a copy on
          // screen is a real card to a shopper and must take a click and a hover.
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.fbClone = '';
          clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          this.track.append(clone);
        }
      }
    }

    removeClones() {
      this.track.querySelectorAll('[data-fb-clone]').forEach((clone) => clone.remove());
    }

    measure() {
      const firstClone = this.track.querySelector('[data-fb-clone]');
      this.setWidth = firstClone ? firstClone.offsetLeft - this.originals[0].offsetLeft : 0;
    }

    wire() {
      this.wired = true;
      const opt = { signal: this.abort.signal };
      this.io = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.kick();
      });
      this.io.observe(this);
      this.ro = new ResizeObserver(() => {
        if (this.isStatic) return;
        this.removeClones();
        this.addClones();
        this.measure();
        this.wrap();
        this.render();
      });
      this.ro.observe(this.viewport);
      document.addEventListener('visibilitychange', () => this.kick(), opt);
      this.viewport.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && FINE_HOVER.matches) {
          this.hovering = true;
          this.kick();
        }
      }, opt);
      this.viewport.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') {
          this.hovering = false;
          this.untilt();
          this.kick();
        }
      }, opt);
      this.addEventListener('focusin', (e) => {
        // Keyboard focus only. A mouse press focuses the card link too; counting that
        // stopped the drift until focus left, and stopped the loop's wrap mid-drag.
        if (!e.target.matches?.(':focus-visible')) return;
        this.focused = true;
        this.kick();
      }, opt);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) {
          this.focused = false;
          this.kick();
        }
      }, opt);
      this.viewport.addEventListener('pointerdown', (e) => this.onDown(e), opt);
      this.viewport.addEventListener('pointermove', (e) => this.onMove(e), opt);
      this.viewport.addEventListener('pointerup', (e) => this.onUp(e), opt);
      this.viewport.addEventListener('pointercancel', (e) => this.onUp(e), opt);
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault(), opt);
      this.viewport.addEventListener('scroll', () => this.absorbScroll(), opt);
      this.viewport.addEventListener('click', (e) => {
        if (this.suppressClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { capture: true, signal: this.abort.signal });
    }

    target() {
      if (this.userPaused || this.hovering || this.focused || this.pressed || this.dragging || !this.inView || document.hidden) return 0;
      return this.speed;
    }

    kick() {
      if (this.isStatic) return;
      this.dataset.state = this.userPaused ? 'paused' : this.target() ? 'running' : 'resting';
      if (!this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame((t) => this.frame(t));
      }
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    frame(now) {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      const target = this.target();
      if (!this.dragging) {
        this.v += (target - this.v) * (1 - Math.exp(-dt / this.ease));
        if (Math.abs(this.v - target) < 0.5) {
          this.v = target;
          this.ease = EASE;
        }
        this.x -= this.v * dt;
        this.wrap();
        this.render();
      }
      if (this.dragging || this.v !== 0 || target !== 0) {
        this.raf = requestAnimationFrame((t) => this.frame(t));
      } else {
        this.raf = 0; // asleep until kick()
      }
    }

    wrap() {
      const w = this.setWidth;
      // While a card has keyboard focus nothing jumps: a wrap would move the focused
      // original off screen and leave its copy (and no focus ring) in its place.
      if (!w || (this.focused && !this.dragging)) return;
      while (this.x <= -w) this.x += w;
      while (this.x > 0) this.x -= w;
    }

    render() {
      this.track.style.transform = `translate3d(${this.x.toFixed(2)}px, 0, 0)`;
    }

    onDown(e) {
      if (this.isStatic || (e.pointerType === 'mouse' && e.button !== 0)) return;
      this.pressed = true;
      this.suppressClick = false;
      this.moved = false;
      this.pointerId = e.pointerId;
      this.pressX = e.clientX;
      this.lastX = e.clientX;
      this.lastT = performance.now();
      this.vel = 0;
      // A press stops the ribbon dead, like a native scroller, so the card under the
      // finger stays under it until the release, even while a flick is coasting.
      this.v = 0;
      this.kick();
    }

    onMove(e) {
      this.tilt(e);
      if (!this.pressed || e.pointerId !== this.pointerId) return;
      if (!this.moved && Math.abs(e.clientX - this.pressX) > DRAG_SLOP) {
        this.moved = true;
        this.dragging = true;
        this.v = 0;
        this.untilt();
        this.viewport.setPointerCapture?.(e.pointerId);
        this.classList.add('is-dragging');
        this.kick();
      }
      if (!this.dragging) return;
      const now = performance.now();
      const dx = e.clientX - this.lastX;
      const dt = Math.max(1, now - this.lastT) / 1000;
      this.vel = 0.8 * (dx / dt) + 0.2 * this.vel; // px/s, positive = rightward
      this.x += dx;
      this.lastX = e.clientX;
      this.lastT = now;
      this.wrap();
      this.render();
    }

    onUp(e) {
      if (!this.pressed || e.pointerId !== this.pointerId) return;
      this.pressed = false;
      if (!this.dragging) return;
      this.dragging = false;
      // Swallow the click that ends a drag. Pointer capture already retargets it in
      // Chromium; this covers browsers that do not. It lapses on the next press.
      this.suppressClick = true;
      clearTimeout(this.suppressTimer);
      this.suppressTimer = setTimeout(() => {
        this.suppressClick = false;
      }, 300);
      this.classList.remove('is-dragging');
      // The speed only comes from moves: a finger that rested before letting go must not
      // fling with the speed it had before it stopped (native scrollers drop it too).
      if (performance.now() - this.lastT > 80) this.vel = 0;
      this.v = Math.max(-MAX_FLING, Math.min(MAX_FLING, -this.vel)); // a leftward flick is positive drift
      this.ease = FLING_EASE;
      this.kick();
    }

    tilt(e) {
      if (!FINE_HOVER.matches || this.dragging || this.isStatic || e.pointerType !== 'mouse') return;
      const card = e.target.closest?.('.fb-fav__card');
      if (this.tilted && this.tilted !== card) this.untilt();
      if (!card) return;
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      card.style.setProperty('--fb-ry', `${(px * 10).toFixed(2)}deg`);
      card.style.setProperty('--fb-rx', `${(-py * 8).toFixed(2)}deg`);
      card.style.setProperty('--fb-mx', `${((px + 0.5) * 100).toFixed(1)}%`);
      card.classList.add('is-tilted');
      this.tilted = card;
    }

    untilt() {
      if (!this.tilted) return;
      this.tilted.classList.remove('is-tilted');
      ['--fb-rx', '--fb-ry', '--fb-mx'].forEach((p) => this.tilted.style.removeProperty(p));
      this.tilted = null;
    }

    absorbScroll() {
      // Keyboard focus makes the browser scroll the viewport to show the card. In drift
      // mode the position lives in the transform, so fold that scroll into x and keep
      // scrollLeft at 0, or the loop maths would be off by it from then on.
      if (this.isStatic || !this.viewport.scrollLeft) return;
      this.x -= this.viewport.scrollLeft;
      this.viewport.scrollLeft = 0;
      this.render();
    }

    step(dir) {
      const item = this.originals[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.viewport.scrollBy({ left: dir * (item.offsetWidth + gap), behavior: REDUCED.matches ? 'auto' : 'smooth' });
    }

    setUserPaused(paused) {
      this.userPaused = paused;
      this.toggle?.setAttribute('aria-pressed', String(paused));
      this.kick();
    }

    // The letter-reveal headline (ported from the RIEWA Projecten hero): a letter shows a
    // favourite's photo clipped to its glyph. Mouse: on hover. Touch, where there is no
    // hover: the letters take turns, every 1.2 s, and stop for the pause button, the static
    // mode (it has no pause button) and while the hero is off screen or the tab hidden.
    setupLetters() {
      this.letters = [...this.querySelectorAll('.fb-letter')];
      if (!this.letters.length) return;
      this.images = this.images || new Map();
      const opt = { signal: this.abort.signal };
      if (FINE_HOVER.matches) {
        this.letters.forEach((letter) => {
          letter.addEventListener('pointerenter', () => this.showLetter(letter), opt);
          letter.addEventListener('pointerleave', () => this.hideLetter(letter), opt);
        });
        return;
      }
      let turn = 0;
      this.letterTimer = setInterval(() => {
        if (this.userPaused || this.isStatic || !this.inView || document.hidden) return;
        this.letters.forEach((l) => this.hideLetter(l));
        this.showLetter(this.letters[turn % this.letters.length]);
        this.loadLetter(this.letters[(turn + 1) % this.letters.length]).catch(() => {}); // the next one is ready in time
        turn += 1;
      }, 1200);
    }

    loadLetter(letter) {
      const src = letter.dataset.fbSrc;
      if (!src) return Promise.reject(new Error('no photo'));
      if (!this.images.has(src)) {
        // A failure is not kept: the next show tries the photo again.
        this.images.set(src, new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(src);
          img.onerror = reject;
          img.src = src;
        }).catch((error) => {
          this.images.delete(src);
          throw error;
        }));
      }
      return this.images.get(src).then(() => {
        const layer = letter.querySelector('.fb-letter__img');
        if (layer && !layer.style.backgroundImage) layer.style.backgroundImage = `url("${src}")`;
      });
    }

    // Only once the photo is there: hiding the base glyph earlier would blank the letter.
    showLetter(letter) {
      letter.wanted = true;
      this.loadLetter(letter).then(() => {
        if (letter.wanted) letter.classList.add('on');
      }, () => {});
    }

    hideLetter(letter) {
      letter.wanted = false;
      letter.classList.remove('on');
    }

    observeBadges() {
      if (!('IntersectionObserver' in window)) return;
      this.badgeIo = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-seen');
          this.badgeIo.unobserve(entry.target);
        }
      }, { root: this.viewport, threshold: 0.6 });
      this.originals.forEach((li) => this.badgeIo.observe(li));
    }
  }

  customElements.define('fb-drift-ribbon', FbDriftRibbon);
})();
